import * as React from 'react'
import * as storage from '@/lib/local-storage'
import { DEFAULT_LOCALE, MESSAGES, SUPPORTED_LOCALES, type Locale } from '@/i18n/messages'

interface TranslateOptions {
  fallback?: string
  values?: Record<string, string | number>
}

interface I18nContextValue {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string, fallbackOrOptions?: string | TranslateOptions, values?: Record<string, string | number>) => string
}

const I18nContext = React.createContext<I18nContextValue | null>(null)

function normalizeLocale(locale: string | null | undefined): Locale {
  if (!locale) return DEFAULT_LOCALE
  const normalized = locale.toLowerCase()
  if (normalized.startsWith('zh')) return 'zh-CN'
  return SUPPORTED_LOCALES.includes(locale as Locale) ? (locale as Locale) : DEFAULT_LOCALE
}

function detectPreferredLocale(): Locale {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE
  const languages = navigator.languages?.length ? navigator.languages : [navigator.language]
  for (const language of languages) {
    const locale = normalizeLocale(language)
    if (locale === 'zh-CN') return locale
  }
  return DEFAULT_LOCALE
}

function formatMessage(template: string, values?: Record<string, string | number>): string {
  if (!values) return template
  return template.replace(/\{(\w+)\}/g, (_, key) => String(values[key] ?? `{${key}}`))
}

function translate(locale: Locale, key: string, options?: TranslateOptions): string {
  const template =
    MESSAGES[locale][key] ??
    MESSAGES.en[key] ??
    options?.fallback ??
    key

  return formatMessage(template, options?.values)
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = React.useState<Locale>(() => {
    const stored = storage.get<string | null>(storage.KEYS.uiLocale, null)
    return normalizeLocale(stored ?? detectPreferredLocale())
  })

  React.useEffect(() => {
    let cancelled = false

    const syncLocale = async () => {
      if (!window.electronAPI?.getUiLocale) return

      try {
        const storedLocale = await window.electronAPI.getUiLocale()
        if (storedLocale) {
          const normalized = normalizeLocale(storedLocale)
          storage.set(storage.KEYS.uiLocale, normalized)
          if (!cancelled) {
            setLocaleState(normalized)
          }
          return
        }

        const fallback = normalizeLocale(storage.get<string | null>(storage.KEYS.uiLocale, null) ?? detectPreferredLocale())
        await window.electronAPI.setUiLocale?.(fallback)
        storage.set(storage.KEYS.uiLocale, fallback)
        if (!cancelled) {
          setLocaleState(fallback)
        }
      } catch (error) {
        console.error('Failed to sync UI locale:', error)
      }
    }

    void syncLocale()

    return () => {
      cancelled = true
    }
  }, [])

  const setLocale = React.useCallback((nextLocale: Locale) => {
    const normalized = normalizeLocale(nextLocale)
    setLocaleState(normalized)
    storage.set(storage.KEYS.uiLocale, normalized)
    void window.electronAPI?.setUiLocale?.(normalized)
  }, [])

  const value = React.useMemo<I18nContextValue>(() => ({
    locale,
    setLocale,
    t: (key, fallbackOrOptions, values) => {
      if (typeof fallbackOrOptions === 'string') {
        return translate(locale, key, { fallback: fallbackOrOptions, values })
      }
      return translate(locale, key, fallbackOrOptions)
    },
  }), [locale, setLocale])

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  )
}

export function useI18n(): I18nContextValue {
  const context = React.useContext(I18nContext)
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider')
  }
  return context
}

export type { Locale }
